import { supabase } from './supabaseClient';

export { supabase };

/**
 * Create or update member profile (upsert)
 */
export const createMemberProfile = async (memberData) => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized (missing env vars).');
    }
    
    // Check if member exists first
    const { data: existingMember } = await supabase
      .from('members')
      .select('stripe_customer_id')
      .eq('email', memberData.email)
      .single();

    const payload = {
      email: memberData.email,
      first_name: memberData.firstName,
      last_name: memberData.lastName,
      allergies: memberData.allergies || [],
      health_goals: memberData.healthGoals || [],
      fitness_level: memberData.fitnessLevel,
      subscription_status: memberData.subscriptionStatus || 'active',
    };

    // Only include stripe_customer_id if provided AND member doesn't already have one
    if (memberData.stripeCustomerId && !existingMember?.stripe_customer_id) {
      payload.stripe_customer_id = memberData.stripeCustomerId;
    }

    // Use upsert: insert if new, update if exists (based on email unique constraint)
    const { error } = await supabase
      .from('members')
      .upsert(payload, { 
        onConflict: 'email',
        ignoreDuplicates: false // Update existing records
      });

    if (error) {
      throw error;
    }
    return { success: true };
  } catch (error) {
    console.error('Error creating/updating member profile:', error);
    return { success: false, error };
  }
};

/**
 * Get member profile by email
 */
export const getMemberByEmail = async (email) => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized (missing env vars).');
    }
    const { data, error } = await supabase
      .from('members')
      .select('*')
      .eq('email', email)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching member:', error);
    return { success: false, error };
  }
};

/**
 * Update member profile
 */
export const updateMemberProfile = async (email, updates) => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized (missing env vars).');
    }
    const { data, error } = await supabase
      .from('members')
      .update(updates)
      .eq('email', email)
      .select('*');

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating member:', error);
    return { success: false, error };
  }
};

/**
 * Get allergy statistics
 */
export const getAllergyStats = async () => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized (missing env vars).');
    }
    const { data, error } = await supabase
      .from('members')
      .select('allergies');

    if (error) throw error;

    // Count occurrences of each allergy
    const allergyCounts = {};
    data.forEach(member => {
      if (member.allergies && Array.isArray(member.allergies)) {
        member.allergies.forEach(allergy => {
          allergyCounts[allergy] = (allergyCounts[allergy] || 0) + 1;
        });
      }
    });

    return { success: true, data: allergyCounts };
  } catch (error) {
    console.error('Error fetching allergy stats:', error);
    return { success: false, error };
  }
};

/**
 * Get health goals statistics
 */
export const getHealthGoalsStats = async () => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized (missing env vars).');
    }
    const { data, error } = await supabase
      .from('members')
      .select('health_goals');

    if (error) throw error;

    // Count occurrences of each goal
    const goalCounts = {};
    data.forEach(member => {
      if (member.health_goals && Array.isArray(member.health_goals)) {
        member.health_goals.forEach(goal => {
          goalCounts[goal] = (goalCounts[goal] || 0) + 1;
        });
      }
    });

    return { success: true, data: goalCounts };
  } catch (error) {
    console.error('Error fetching health goals stats:', error);
    return { success: false, error };
  }
};

/**
 * Get fitness level distribution
 */
export const getFitnessLevelStats = async () => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized (missing env vars).');
    }
    const { data, error } = await supabase
      .from('members')
      .select('fitness_level');

    if (error) throw error;

    // Count occurrences of each fitness level
    const levelCounts = {};
    data.forEach(member => {
      if (member.fitness_level) {
        levelCounts[member.fitness_level] = (levelCounts[member.fitness_level] || 0) + 1;
      }
    });

    return { success: true, data: levelCounts };
  } catch (error) {
    console.error('Error fetching fitness level stats:', error);
    return { success: false, error };
  }
};

/**
 * Get total member count
 */
export const getMemberCount = async () => {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized (missing env vars).');
    }
    const { count, error } = await supabase
      .from('members')
      .select('*', { count: 'exact', head: true });

    if (error) throw error;
    return { success: true, count };
  } catch (error) {
    console.error('Error fetching member count:', error);
    return { success: false, error };
  }
};
